import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import type { TaskTimeEntry, NewTaskTimeEntry } from '../types';
import { getCurrentUserOrganizationId } from './referenceDataCache';

/**
 * Tempo lavorato sulle task di progetto.
 *
 * Differenza voluta rispetto a ticketTimeEntryStore: qui il timer acceso è una riga nel
 * database con ended_at nullo, non una chiave in localStorage. Così sopravvive al cambio di
 * dispositivo, e l'indice unico parziale su task_time_entries impedisce due timer aperti dallo
 * stesso utente anche se due schede provano ad avviarli insieme.
 */

interface ActiveTimer {
  entryId: string;
  taskId: string;
  startedAt: string;
}

interface TaskTimeEntryStore {
  entriesByTask: Record<string, TaskTimeEntry[]>;
  activeTimer: ActiveTimer | null;
  loading: boolean;
  error: string | null;

  fetchEntries: (taskId: string) => Promise<void>;
  fetchActiveTimer: () => Promise<void>;
  startTimer: (taskId: string, projectId: string) => Promise<void>;
  stopTimer: (description?: string) => Promise<void>;
  createEntry: (input: NewTaskTimeEntry) => Promise<TaskTimeEntry | null>;
  updateEntry: (id: string, updates: Partial<TaskTimeEntry>) => Promise<void>;
  deleteEntry: (id: string) => Promise<void>;
  totalMinutes: (taskId: string) => number;
}

const SELECT = `
  id, organization_id, task_id, project_id, user_id, started_at, ended_at,
  duration_minutes, description, counts_toward_budget, created_at, updated_at,
  user:user_profiles!task_time_entries_user_id_fkey (id, full_name, avatar_url, profile_image_url)
`;

export const useTaskTimeEntryStore = create<TaskTimeEntryStore>((set, get) => ({
  entriesByTask: {},
  activeTimer: null,
  loading: false,
  error: null,

  fetchEntries: async (taskId) => {
    set({ loading: true, error: null });

    const { data, error } = await supabase
      .from('task_time_entries')
      .select(SELECT)
      .eq('task_id', taskId)
      .order('started_at', { ascending: false });

    if (error) {
      console.error('Errore nel caricamento delle voci di tempo:', error);
      set({ loading: false, error: friendlyDbError(error) });
      return;
    }

    set((s) => ({
      entriesByTask: { ...s.entriesByTask, [taskId]: (data ?? []) as unknown as TaskTimeEntry[] },
      loading: false
    }));
  },

  fetchActiveTimer: async () => {
    const { data: auth } = await supabase.auth.getUser();
    if (!auth?.user) return;

    const { data } = await supabase
      .from('task_time_entries')
      .select('id, task_id, started_at')
      .eq('user_id', auth.user.id)
      .is('ended_at', null)
      .maybeSingle();

    set({
      activeTimer: data
        ? { entryId: data.id, taskId: data.task_id, startedAt: data.started_at }
        : null
    });
  },

  startTimer: async (taskId, projectId) => {
    // Un timer già acceso altrove va chiuso: l'indice unico lo impedirebbe comunque,
    // ma così l'utente non perde il tempo maturato sull'altra task.
    if (get().activeTimer) await get().stopTimer();

    const { data: auth } = await supabase.auth.getUser();
    if (!auth?.user) return;

    const organizationId = await getCurrentUserOrganizationId();

    const { data, error } = await supabase
      .from('task_time_entries')
      .insert({
        organization_id: organizationId,
        task_id: taskId,
        project_id: projectId,
        user_id: auth.user.id,
        started_at: new Date().toISOString()
      })
      .select(SELECT)
      .single();

    if (error || !data) {
      console.error('Errore nell\'avvio del timer:', error);
      set({ error: error?.message ?? 'Impossibile avviare il timer' });
      return;
    }

    const entry = data as unknown as TaskTimeEntry;
    set((s) => ({
      activeTimer: { entryId: entry.id, taskId, startedAt: entry.started_at },
      entriesByTask: {
        ...s.entriesByTask,
        [taskId]: [entry, ...(s.entriesByTask[taskId] ?? [])]
      },
      error: null
    }));
  },

  stopTimer: async (description) => {
    const { activeTimer } = get();
    if (!activeTimer) return;

    const endedAt = new Date();
    // Un timer fermato subito vale comunque un minuto: zero renderebbe la voce invisibile.
    const minutes = Math.max(
      1,
      Math.round((endedAt.getTime() - new Date(activeTimer.startedAt).getTime()) / 60000)
    );

    const { error } = await supabase
      .from('task_time_entries')
      .update({
        ended_at: endedAt.toISOString(),
        duration_minutes: minutes,
        ...(description ? { description } : {})
      })
      .eq('id', activeTimer.entryId);

    if (error) {
      console.error('Errore nella chiusura del timer:', error);
      set({ error: friendlyDbError(error) });
      return;
    }

    set({ activeTimer: null, error: null });
    await get().fetchEntries(activeTimer.taskId);
  },

  createEntry: async (input) => {
    const { data: auth } = await supabase.auth.getUser();
    if (!auth?.user) return null;

    const organizationId = await getCurrentUserOrganizationId();

    const { data, error } = await supabase
      .from('task_time_entries')
      .insert({ ...input, organization_id: organizationId, user_id: auth.user.id })
      .select(SELECT)
      .single();

    if (error || !data) {
      console.error('Errore nella registrazione del tempo:', error);
      set({ error: error?.message ?? 'Impossibile registrare il tempo' });
      return null;
    }

    const entry = data as unknown as TaskTimeEntry;
    set((s) => ({
      entriesByTask: {
        ...s.entriesByTask,
        [input.task_id]: [entry, ...(s.entriesByTask[input.task_id] ?? [])]
      },
      error: null
    }));
    return entry;
  },

  updateEntry: async (id, updates) => {
    // Correggere la durata senza toccare gli estremi lascerebbe una voce che dichiara 45 minuti
    // fra due orari distanti due ore: i totali resterebbero giusti, ma chiunque ricalcolasse la
    // durata dagli orari otterrebbe un altro numero. Si tiene la fine e si sposta l'inizio.
    let payload = updates;
    if (typeof updates.duration_minutes === 'number' && updates.started_at === undefined) {
      const entry = Object.values(get().entriesByTask)
        .flat()
        .find((e) => e.id === id);

      if (entry?.ended_at) {
        const fine = new Date(entry.ended_at).getTime();
        payload = {
          ...updates,
          started_at: new Date(fine - updates.duration_minutes * 60000).toISOString()
        };
      }
    }

    const { data, error } = await supabase
      .from('task_time_entries')
      .update(payload)
      .eq('id', id)
      .select('task_id')
      .single();

    if (error || !data) {
      console.error('Errore nell\'aggiornamento della voce:', error);
      set({ error: error?.message ?? 'Impossibile aggiornare la voce' });
      return;
    }

    await get().fetchEntries(data.task_id);
  },

  deleteEntry: async (id) => {
    const entry = Object.values(get().entriesByTask).flat().find((e) => e.id === id);

    const { error } = await supabase.from('task_time_entries').delete().eq('id', id);

    if (error) {
      console.error('Errore nella cancellazione della voce:', error);
      set({ error: friendlyDbError(error) });
      return;
    }

    // Cancellare la voce del timer in corso lascerebbe il cronometro acceso a vuoto.
    set((s) => ({
      activeTimer: s.activeTimer?.entryId === id ? null : s.activeTimer,
      error: null
    }));

    if (entry) await get().fetchEntries(entry.task_id);
  },

  /** Somma dei minuti delle sole voci chiuse: una voce in corso non deve gonfiare il totale. */
  totalMinutes: (taskId) =>
    (get().entriesByTask[taskId] ?? [])
      .filter((e) => e.ended_at !== null)
      .reduce((sum, e) => sum + (e.duration_minutes ?? 0), 0)
}));
